import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ChartBarIcon,
  UserGroupIcon, 
  ChatBubbleLeftRightIcon,
  CursorArrowRaysIcon,
  TrophyIcon,
  LinkIcon,
  SparklesIcon,
  PresentationChartLineIcon,
  HeartIcon,
  ArrowPathRoundedSquareIcon,
  ClipboardDocumentCheckIcon,
  BeakerIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import ChatbotHealthTab from '../components/ChatbotHealthTab';
import FollowbackHealthTab from '../components/FollowbackHealthTab';
import LedgerHealthTab from '../components/LedgerHealthTab';
import OpsHealthTab from '../components/OpsHealthTab';
import OverallHealthTab from '../components/OverallHealthTab';

const ANALYTICS_VIEWS = [
  { id: 'overview', label: 'Overview', icon: PresentationChartLineIcon },
  { id: 'overall-health', label: 'Overall health', icon: ShieldCheckIcon, adminOnly: true },
  { id: 'ops-health', label: 'Ops Health', icon: BeakerIcon, adminOnly: true },
  { id: 'chatbot-health', label: 'Chatbot Health', icon: HeartIcon },
  { id: 'followback-health', label: 'Followback Health', icon: ArrowPathRoundedSquareIcon, adminOnly: true },
  { id: 'ledger-health', label: 'Ledger Health', icon: ClipboardDocumentCheckIcon, adminOnly: true }
];

const Analytics = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const visibleViews = ANALYTICS_VIEWS.filter((view) => !view.adminOnly || user?.role === 'admin');
  const resolveInitialView = () => {
    const fromUrl = searchParams.get('view');
    if (fromUrl && visibleViews.some((v) => v.id === fromUrl)) return fromUrl;
    return 'overview';
  };
  const [dashboardData, setDashboardData] = useState(null);
  const [ctaAnalytics, setCtaAnalytics] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [models, setModels] = useState([]);
  const [presets, setPresets] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [phaseStats, setPhaseStats] = useState(null);
  const [phaseStatsLoading, setPhaseStatsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [massDMAnalytics, setMassDMAnalytics] = useState(null);
  const [massDMAnalyticsLoading, setMassDMAnalyticsLoading] = useState(false);
  const [massDMAnalyticsDisplayCount, setMassDMAnalyticsDisplayCount] = useState(5);
  const [linkAnalytics, setLinkAnalytics] = useState(null);
  const [linkAnalyticsLoading, setLinkAnalyticsLoading] = useState(false);
  const [followUpAnalytics, setFollowUpAnalytics] = useState(null);
  const [followUpAnalyticsLoading, setFollowUpAnalyticsLoading] = useState(false);
  const [followUpAnalyticsDisplayCount, setFollowUpAnalyticsDisplayCount] = useState(5);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [activePreset, setActivePreset] = useState(null);
  const [scrapeTag, setScrapeTag] = useState('all');
  const [scrapeTagOptions, setScrapeTagOptions] = useState({
    tags: [],
    untaggedCount: 0,
    totalConversations: 0,
  });
  const [activeView, setActiveView] = useState(resolveInitialView);
  const [startDate, endDate] = dateRange;

  const switchView = (viewId) => {
    setActiveView(viewId);
    const next = new URLSearchParams(searchParams);
    if (viewId === 'overview') next.delete('view');
    else next.set('view', viewId);
    setSearchParams(next, { replace: true });
  };

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };

  // Sync dateRange with dateFrom/dateTo when dateRange changes (from calendar)
  useEffect(() => {
    if (startDate && endDate) {
      const fromStr = formatLocalDate(startDate);
      const toStr = formatLocalDate(endDate);
      // Only update if different to avoid loops
      if (dateFrom !== fromStr || dateTo !== toStr) {
        setDateFrom(fromStr);
        setDateTo(toStr);
      }
    } else if (!startDate && !endDate && (dateFrom || dateTo)) {
      // Clear if calendar is cleared
      setDateFrom('');
      setDateTo('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Sync dateRange when dateFrom/dateTo change from presets
  useEffect(() => {
    if (dateFrom && dateTo) {
      const from = parseLocalDate(dateFrom);
      const to = parseLocalDate(dateTo);
      if (!from || !to) return;
      // Only update if different to avoid loops
      if (!startDate || !endDate || 
          from.getTime() !== startDate.getTime() || 
          to.getTime() !== endDate.getTime()) {
        setDateRange([from, to]);
      }
    } else if (!dateFrom && !dateTo && (startDate || endDate)) {
      // Clear calendar if dates are cleared
      setDateRange([null, null]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (activeView !== 'overview') return;
    fetchOverviewData();
    fetchModels();
    fetchPresets();
    fetchScrapeTagOptions();
    if (user?.features?.canUseMassDM) {
      fetchMassDMAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dateFrom, dateTo, scrapeTag, activeView]);

  useEffect(() => {
    if (activeView !== 'overview' || !selectedModelId) {
      if (!selectedModelId) {
        setPhaseStats(null);
        setLinkAnalytics(null);
        setFollowUpAnalytics(null);
        setSelectedModel(null);
      }
      return;
    }
    fetchPhaseStats(selectedModelId);
    fetchLinkAnalytics(selectedModelId);
    fetchFollowUpAnalytics(selectedModelId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModelId, dateFrom, dateTo, scrapeTag, activeView]);

  const fetchScrapeTagOptions = async () => {
    try {
      const params = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const convRes = await api.get('/analytics/scrape-tags', { params }).catch(() => null);
      const tags = Array.isArray(convRes?.data?.tags) ? convRes.data.tags : [];
      setScrapeTagOptions({
        tags: tags.map((t) => ({
          name: t.name,
          count: Number(t.count) || 0,
          ctaShared: Number(t.ctaShared) || 0,
        })),
        untaggedCount: Number(convRes?.data?.untaggedCount) || 0,
        totalConversations: Number(convRes?.data?.totalConversations) || 0,
      });
    } catch (_) { /* optional */ }
  };

  const fetchOverviewData = async () => {
    try {
      setOverviewLoading(true);
      const params = { includeToday: 'false' };
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (scrapeTag && scrapeTag !== 'all') params.scrapeTag = scrapeTag;

      const [dashboardRes, ctaRes] = await Promise.all([
        api.get('/analytics/dashboard', { params }),
        api.get('/cta-analytics/best-performing', { params: { limit: 20, ...params } })
      ]);

      setDashboardData(dashboardRes.data);
      const ctaData = ctaRes.data?.data || ctaRes.data;
      setCtaAnalytics(Array.isArray(ctaData) ? { bestPerformingScripts: ctaData } : ctaData);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setOverviewLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      const response = await api.get('/models', { params: { minimal: '1' } });
      setModels(response.data);
      if (response.data.length > 0) {
        setSelectedModelId((prev) => prev || response.data[0]._id);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  const fetchPresets = async () => {
    try {
      const response = await api.get('/models/presets/list');
      setPresets(response.data);
    } catch (error) {
      console.error('Error fetching presets:', error);
    }
  };

  // Helper function to get preset name from preset key
  const getPresetName = (presetKey) => {
    if (!presetKey) return null;
    const preset = presets.find(p => p.key === presetKey);
    return preset ? preset.name : presetKey;
  };

  const fetchPhaseStats = async (modelId) => {
    try {
      setPhaseStatsLoading(true);
      // Build query parameters for date filtering
      const params = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (scrapeTag && scrapeTag !== 'all') params.scrapeTag = scrapeTag;
      
      // Fetch phase stats
      const response = await api.get(`/analytics/phases/${modelId}`, { params });
      setPhaseStats(response.data);
      setSelectedModel(response.data.modelConfig || null);
    } catch (error) {
      console.error('Error fetching phase statistics:', error);
      setPhaseStats(null);
      setSelectedModel(null);
    } finally {
      setPhaseStatsLoading(false);
    }
  };

  const fetchLinkAnalytics = async (modelId) => {
    try {
      setLinkAnalyticsLoading(true);
      const response = await api.get(`/models/${modelId}/link-analytics`);
      setLinkAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching link analytics:', error);
      setLinkAnalytics(null);
    } finally {
      setLinkAnalyticsLoading(false);
    }
  };

  const fetchFollowUpAnalytics = async (modelId) => {
    try {
      setFollowUpAnalyticsLoading(true);
      const params = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      
      const response = await api.get(`/analytics/follow-ups/${modelId}`, { params });
      setFollowUpAnalytics(response.data?.data || response.data);
      setFollowUpAnalyticsDisplayCount(5);
    } catch (error) {
      console.error('Error fetching follow-up analytics:', error);
      setFollowUpAnalytics(null);
    } finally {
      setFollowUpAnalyticsLoading(false);
    }
  };

  const fetchMassDMAnalytics = async () => {
    try {
      setMassDMAnalyticsLoading(true);
      const response = await api.get('/mass-dm/analytics');
      setMassDMAnalytics(response.data);
      // Reset display count when new data is loaded
      setMassDMAnalyticsDisplayCount(5);
    } catch (error) {
      console.error('Error fetching mass DM analytics:', error);
      setMassDMAnalytics(null);
    } finally {
      setMassDMAnalyticsLoading(false);
    }
  };

  const handleDateFilterChange = () => {
    // No-op: date changes already trigger refetch via useEffect.
  };

  const clearDateFilter = () => {
    setDateFrom('');
    setDateTo('');
    setDateRange([null, null]);
    setActivePreset(null);
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue', loading = false }) => (
    <div className="card">
      <div className="card-body">
        <div className="flex items-center">
          <div className={`flex-shrink-0 p-3 rounded-md bg-${color}-100 dark:bg-${color}-900/20`}>
            <Icon className={`h-6 w-6 text-${color}-600 dark:text-${color}-400`} />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
            {loading ? (
              <div className="loading-spinner h-7 w-7 mt-1" />
            ) : (
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const setPresetDateRange = (preset) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    let fromDate = '';
    let toDate = formatLocalDate(today);

    switch (preset) {
      case 'today': {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        fromDate = formatLocalDate(todayStart);
        break;
      }
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
        fromDate = formatLocalDate(yesterday);
        toDate = formatLocalDate(yesterday);
        break;
      }
      case 'last7days': {
        const last7Days = new Date(today);
        last7Days.setDate(last7Days.getDate() - 7);
        last7Days.setHours(0, 0, 0, 0);
        fromDate = formatLocalDate(last7Days);
        break;
      }
      case 'last30days': {
        const last30Days = new Date(today);
        last30Days.setDate(last30Days.getDate() - 30);
        last30Days.setHours(0, 0, 0, 0);
        fromDate = formatLocalDate(last30Days);
        break;
      }
      case 'thisMonth': {
        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        fromDate = formatLocalDate(thisMonthStart);
        break;
      }
      case 'lastMonth': {
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        fromDate = formatLocalDate(lastMonthStart);
        toDate = formatLocalDate(lastMonthEnd);
        break;
      }
      case 'thisYear': {
        const thisYearStart = new Date(today.getFullYear(), 0, 1);
        fromDate = formatLocalDate(thisYearStart);
        break;
      }
      default:
        return;
    }

    setActivePreset(preset);
    setDateFrom(fromDate);
    setDateTo(toDate);
  };

  return (
    <div className="space-y-6">
      {/* Hero Banner */}
      <div className={`relative overflow-hidden rounded-2xl ${
        isDarkMode 
          ? 'bg-gradient-to-l from-slate-900 via-blue-950 to-slate-900' 
          : 'bg-gradient-to-l from-sky-50 via-blue-100 to-sky-100'
      }`}>
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient orbs */}
          <div className={`absolute -top-24 -right-16 w-80 h-80 rounded-full blur-3xl ${
            isDarkMode ? 'bg-blue-600/20' : 'bg-blue-300/40'
          }`} />
          <div className={`absolute top-1/2 -left-20 w-72 h-72 rounded-full blur-3xl ${
            isDarkMode ? 'bg-sky-600/15' : 'bg-sky-300/30'
          }`} />
          <div className={`absolute -bottom-16 right-1/3 w-56 h-56 rounded-full blur-3xl ${
            isDarkMode ? 'bg-cyan-600/10' : 'bg-cyan-200/30'
          }`} />
          
          {/* Grid pattern overlay */}
          <div 
            className={`absolute inset-0 ${isDarkMode ? 'opacity-[0.03]' : 'opacity-[0.08]'}`}
            style={{
              backgroundImage: `linear-gradient(${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
          
          {/* Floating decorative shapes - chart-like */}
          <svg className="absolute top-6 right-16 w-24 h-24 opacity-10" viewBox="0 0 100 100">
            <polyline points="10,80 30,50 50,65 70,30 90,45" fill="none" stroke={isDarkMode ? '#93c5fd' : '#3b82f6'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <svg className="absolute bottom-8 left-20 w-16 h-16 opacity-10" viewBox="0 0 100 100">
            <rect x="10" y="50" width="15" height="40" fill={isDarkMode ? '#7dd3fc' : '#0284c7'} opacity="0.3" />
            <rect x="30" y="30" width="15" height="60" fill={isDarkMode ? '#7dd3fc' : '#0284c7'} opacity="0.4" />
            <rect x="50" y="40" width="15" height="50" fill={isDarkMode ? '#7dd3fc' : '#0284c7'} opacity="0.5" />
            <rect x="70" y="20" width="15" height="70" fill={isDarkMode ? '#7dd3fc' : '#0284c7'} opacity="0.6" />
          </svg>
          <svg className="absolute top-1/2 left-8 w-12 h-12 opacity-10" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" stroke={isDarkMode ? '#67e8f9' : '#0891b2'} strokeWidth="2" strokeDasharray="10 5" />
          </svg>
        </div>
        
        {/* Content */}
        <div className="relative px-8 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              {/* Icon/Logo area */}
              <div className={`relative flex items-center justify-center w-16 h-16 rounded-2xl ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-blue-500 to-sky-600 shadow-lg shadow-blue-500/30' 
                  : 'bg-gradient-to-br from-blue-500 to-sky-500 shadow-lg shadow-blue-400/40'
              }`}>
                <PresentationChartLineIcon className="w-8 h-8 text-white" />
                <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                  isDarkMode ? 'bg-cyan-400' : 'bg-cyan-500'
                }`}>
                  <SparklesIcon className="w-3 h-3 text-white" />
                </div>
              </div>
              
              {/* Text content */}
              <div>
                <h1 className={`text-3xl font-bold tracking-tight ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  Analytics
                </h1>
                <p className={`mt-1.5 text-base ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  Monitor your automation performance and activity
                </p>
                {activeView === 'overview' && dashboardData && !overviewLoading && (
                  <div className={`mt-3 flex items-center space-x-4 text-sm ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}>
                    <span className="flex items-center">
                      <span className={`w-2 h-2 rounded-full mr-2 ${
                        isDarkMode ? 'bg-emerald-400' : 'bg-emerald-500'
                      }`} />
                      {dashboardData.totalResponses?.toLocaleString() || 0} Responses
                    </span>
                    <span className="flex items-center">
                      <span className={`w-2 h-2 rounded-full mr-2 ${
                        isDarkMode ? 'bg-blue-400' : 'bg-blue-500'
                      }`} />
                      {dashboardData.totalCTAs?.toLocaleString() || 0} CTAs
                    </span>
                  </div>
                )}
                {activeView === 'overview' && overviewLoading && (
                  <div className={`mt-3 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    Loading stats…
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View tabs */}
      <div className={`flex items-center gap-1 p-1 rounded-xl w-fit ${
        isDarkMode ? 'bg-slate-800/80' : 'bg-slate-100'
      }`}>
        {visibleViews.map((view) => (
          <button
            key={view.id}
            onClick={() => switchView(view.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeView === view.id
                ? isDarkMode
                  ? 'bg-slate-700 text-white shadow'
                  : 'bg-white text-slate-900 shadow'
                : isDarkMode
                  ? 'text-slate-400 hover:text-slate-200'
                  : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <view.icon className="w-4 h-4" />
            {view.label}
          </button>
        ))}
      </div>

      {activeView === 'overall-health' && user?.role === 'admin' && <OverallHealthTab />}

      {activeView === 'ops-health' && user?.role === 'admin' && <OpsHealthTab />}

      {activeView === 'chatbot-health' && <ChatbotHealthTab />}

      {activeView === 'followback-health' && user?.role === 'admin' && <FollowbackHealthTab />}

      {activeView === 'ledger-health' && user?.role === 'admin' && <LedgerHealthTab />}

      {activeView === 'overview' && (
      <>
      {/* Date Range Filter */}
      <div className="card overflow-visible">
        <div className="card-body overflow-visible">
          <div className="space-y-4">
            {/* Preset Date Range Buttons */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Quick Select
              </label>
              <div className="flex flex-wrap gap-2 items-center">
                {[
                  { key: 'today', label: 'Today' },
                  { key: 'yesterday', label: 'Yesterday' },
                  { key: 'last7days', label: 'Last 7 Days' },
                  { key: 'last30days', label: 'Last 30 Days' },
                  { key: 'thisMonth', label: 'This Month' },
                  { key: 'lastMonth', label: 'Last Month' },
                  { key: 'thisYear', label: 'This Year' },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setPresetDateRange(key)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-xl transition-colors ${
                      activePreset === key
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {label}
                  </button>
                ))}
                {(dateFrom || dateTo) && (
                  <button
                    onClick={clearDateFilter}
                    className="px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-xl transition-colors"
                  >
                    Clear Filter
                  </button>
                )}
                <label className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Category
                </label>
                <select
                  value={scrapeTag}
                  onChange={(e) => setScrapeTag(e.target.value)}
                  className="form-input text-sm py-1.5 max-w-[16rem]"
                  title="Filter conversations / CTAs by scrape data category"
                >
                  <option value="all">
                    All categories ({Number(scrapeTagOptions.totalConversations || 0).toLocaleString()})
                  </option>
                  <option value="__untagged__">
                    Untagged / legacy ({Number(scrapeTagOptions.untaggedCount || 0).toLocaleString()})
                  </option>
                  {(scrapeTagOptions.tags || []).map((tag) => (
                    <option key={tag.name} value={tag.name}>
                      {tag.name} ({Number(tag.count || 0).toLocaleString()})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Custom Date Range - Calendar Picker */}
            <div className="overflow-visible">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Custom Date Range
              </label>
              <div className="flex justify-center">
                <div className="relative w-full max-w-2xl overflow-visible">
                  <DatePicker
                    selectsRange
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(update) => {
                      setDateRange(update);
                      setActivePreset(null); // custom selection clears preset highlight
                      if (update[0] && update[1]) {
                        setTimeout(handleDateFilterChange, 300);
                      }
                    }}
                    isClearable={true}
                    customInput={
                      <button
                        type="button"
                        className={`w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-center cursor-pointer transition-colors ${
                          startDate || endDate ? 'font-medium' : ''
                        }`}
                      >
                        {startDate && endDate 
                          ? `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                          : startDate
                            ? `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - Select end date...`
                            : 'Click to select date range'}
                      </button>
                    }
                    dateFormat="MMM d, yyyy"
                    maxDate={new Date()}
                    monthsShown={2}
                    calendarClassName="dark:bg-gray-800 dark:text-gray-100 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700"
                    wrapperClassName="w-full"
                    withPortal
                    portalId="root"
                    popperPlacement="bottom-start"
                    popperModifiers={[
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 8],
                        },
                      },
                      {
                        name: 'preventOverflow',
                        options: {
                          rootBoundary: 'viewport',
                          boundary: 'viewport',
                          tether: true,
                          altAxis: true,
                          padding: 8,
                        },
                      },
                      {
                        name: 'flip',
                        options: {
                          fallbackPlacements: ['bottom-start', 'bottom', 'top-start', 'top'],
                        },
                      },
                    ]}
                    popperClassName="react-datepicker-popper z-[9999]"
                  />
                </div>
              </div>
            </div>

            {/* Active Filter Display */}
            {(dateFrom || dateTo) && (
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Active Filter:</span>{' '}
                    {dateFrom && dateTo 
                      ? `${new Date(dateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${new Date(dateTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                      : dateFrom 
                        ? `From ${new Date(dateFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                        : `Until ${new Date(dateTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                    }
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Model Selector */}
      <div className="card">
        <div className="card-body">
          <label htmlFor="model-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Model
          </label>
          <select
            id="model-select"
            value={selectedModelId || ''}
            onChange={(e) => setSelectedModelId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">-- Select a model --</option>
            {models.map((model) => {
              const presetName = getPresetName(model.preset);
              return (
              <option key={model._id} value={model._id}>
                  {model.displayName || model.name}{model.displayName ? ` (${model.name})` : ''}{presetName ? ` • ${presetName}` : ''}{user?.role === 'admin' && model.userId?.username ? ` - ${model.userId.username}` : ''}
              </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* Phase Statistics */}
      {selectedModelId && (() => {
        // ─── Per-version breakdown (v2 / v3 / …) ────────────────────────────
        // The backend returns a `versions` array with one entry per
        // chatbotReleaseSlug when the model has traffic on more than one
        // release. v2 and v3 use fundamentally different phase spaces so
        // averaging them together was hiding a broken v3 CTA path — we now
        // render one distribution card per version. When only a single
        // release is live, the merged view is shown (unchanged behaviour).
        const versions = Array.isArray(phaseStats?.versions) ? phaseStats.versions : [];
        const versionsWithData = versions.filter((v) => (v?.totalConversations || 0) > 0);
        const splitByVersion = versionsWithData.length >= 2;

        const versionLabel = (slug) => {
          if (!slug) return 'Legacy';
          const s = String(slug).toLowerCase();
          if (s === 'legacy') return 'Legacy (no version)';
          return s.startsWith('v') ? s.toUpperCase() : s;
        };

        // Given a phase-distribution slice (either the merged view or one
        // version's slice) render the gradient bar + labels + summary card.
        // `versionSlug` is only passed when rendering a per-version slice —
        // it drives the sub-header shown above the gradient.
        const renderPhaseDistribution = (data, versionSlug) => {
          if (!data) return null;

          // Build the ordered phase list.
          //
          // In split-by-version mode the backend hands us `usedPhaseKeys` —
          // the definitional phase space for that release (e.g. v3 only
          // uses initial/phase_2/phase_3). We render exactly those bars
          // in order, even if some counts are 0, so each version's card
          // reflects its OWN phase structure — not the model's.
          //
          // In merged / single-version mode we fall back to deriving the
          // phase list from the Amy model config (behaviour before the
          // per-version split existed).
          const shortenLabel = (name) =>
            name ? name.replace(/Post-CTA Heat Build\s+(\d+)/i, 'P-CTA $1')
                        .replace(/Post-CTA\s+(\d+)/i, 'P-CTA $1') : name;

          const labelFor = (key) => {
            if (key === 'initial') return 'Initial';
            if (key === 'cta') return 'CTA';
            if (key === 'completed') return 'Completed';
            if (key === 'phase_6' || key === 'phase_7') {
              const postCTAPhases = selectedModel?.postCTAPhases || [];
              const num = key === 'phase_6' ? 6 : 7;
              const cfg = postCTAPhases.find((p) => p.phaseNumber === num);
              return shortenLabel(cfg?.name) || (key === 'phase_6' ? 'P-CTA 1' : 'P-CTA 2');
            }
            const m = /^phase_(\d+)$/.exec(key);
            if (m) return `Phase ${m[1]}`;
            return key;
          };

          let orderedPhases;
          if (versionSlug && Array.isArray(data.usedPhaseKeys) && data.usedPhaseKeys.length > 0) {
            // Version-specific phase space from the backend
            orderedPhases = data.usedPhaseKeys.map((key) => ({
              key,
              label: labelFor(key),
              count: data.phases?.[key] || 0,
            }));
          } else {
            // Merged / single-version fallback — derive from model config
            orderedPhases = [];
            orderedPhases.push({ key: 'initial', label: 'Initial', count: data.phases?.initial || 0 });
            const numberOfPhases = selectedModel?.numberOfPhases || 0;
            for (let i = 1; i <= numberOfPhases; i++) {
              const phaseKey = `phase_${i}`;
              orderedPhases.push({ key: phaseKey, label: `Phase ${i}`, count: data.phases?.[phaseKey] || 0 });
            }
            orderedPhases.push({ key: 'cta', label: 'CTA', count: data.phases?.cta || 0 });
            if (selectedModel?.enablePostCTAPhases && selectedModel?.postCTAPhases) {
              const postCTAPhases = selectedModel.postCTAPhases || [];
              postCTAPhases.forEach((postPhase) => {
                if (postPhase.phaseNumber === 6) {
                  orderedPhases.push({ key: 'phase_6', label: shortenLabel(postPhase.name) || 'P-CTA 1', count: data.phases?.phase_6 || 0 });
                } else if (postPhase.phaseNumber === 7) {
                  orderedPhases.push({ key: 'phase_7', label: shortenLabel(postPhase.name) || 'P-CTA 2', count: data.phases?.phase_7 || 0 });
                }
              });
            }
            orderedPhases.push({ key: 'completed', label: 'Completed', count: data.phases?.completed || 0 });
          }

          const total = orderedPhases.reduce((sum, phase) => sum + phase.count, 0);
          const phasesWithPercent = orderedPhases.map((phase) => ({
            ...phase,
            percentage: total > 0 ? (phase.count / total) * 100 : 0,
          }));

          let cumulativePercent = 0;
          const phasesWithPositions = phasesWithPercent.map((phase) => {
            const startPercent = cumulativePercent;
            cumulativePercent += phase.percentage;
            return { ...phase, startPercent, endPercent: cumulativePercent, centerPercent: startPercent + (phase.percentage / 2) };
          });

          const getColorForPhase = (phaseKey) => {
            switch (phaseKey) {
              case 'initial': return '#3b82f6';
              case 'phase_1': return '#eab308';
              case 'phase_2': return '#f97316';
              case 'phase_3': return '#ea580c';
              case 'phase_4': return '#ef4444';
              case 'phase_5': return '#dc2626';
              case 'phase_6': return '#991b1b';
              case 'phase_7': return '#7f1d1d';
              case 'phase_8': return '#7f1d1d';
              case 'phase_9': return '#7f1d1d';
              case 'phase_10': return '#7f1d1d';
              case 'cta': return '#dc2626';
              default:
                if (phaseKey.startsWith('post_cta_phase_') || phaseKey.startsWith('postCTA_phase_')) return '#991b1b';
                return '#6b7280';
            }
          };

          const activePhases = phasesWithPositions.filter((p) => p.count > 0);
          const gradientStops = [];
          if (activePhases.length > 0) {
            activePhases.forEach((phase, index) => {
              const color = getColorForPhase(phase.key);
              if (index === 0) gradientStops.push(`${color} ${phase.startPercent}%`);
              gradientStops.push(`${color} ${phase.endPercent}%`);
            });
          }

          const shareRate = (data.totalConversations || 0) > 0
            ? ((data.ctasShared || 0) / data.totalConversations) * 100
            : 0;

          return (
            <div className="space-y-6" key={versionSlug || 'merged'}>
              {/* Per-version sub-header — only in split mode */}
              {versionSlug && (
                <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-2">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-semibold bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                      {versionLabel(versionSlug)}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {data.totalConversations || 0} conversations · {data.newConversations || 0} new · {(data.ctasShared || 0)} CTA{data.ctasShared === 1 ? '' : 's'} shared ({shareRate.toFixed(1)}%)
                    </span>
                  </div>
                </div>
              )}

              {/* Phase Progress Bar */}
              {activePhases.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Conversation Distribution by Phase</h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Total: {total}</span>
                  </div>

                  <div className="w-full h-6 progress-bg rounded-full overflow-hidden relative">
                    <div
                      className="h-full rounded-full relative"
                      style={{ width: '100%', background: `linear-gradient(to right, ${gradientStops.join(', ')})` }}
                    >
                      {phasesWithPositions.map((phase) => {
                        if (phase.count === 0) return null;
                        const actualWidth = phase.percentage;
                        return (
                          <div
                            key={phase.key}
                            className="absolute inset-0 flex items-center justify-center pointer-events-none"
                            style={{ left: `${phase.startPercent}%`, width: `${actualWidth}%` }}
                            title={`${phase.label}: ${phase.count} (${phase.percentage.toFixed(1)}%)`}
                          >
                            {actualWidth >= 8 && (
                              <span className="text-xs font-bold text-white drop-shadow-sm whitespace-nowrap">{phase.count}</span>
                            )}
                            {actualWidth < 8 && actualWidth >= 4 && (
                              <span className="text-xs font-bold text-white drop-shadow-sm whitespace-nowrap">{phase.count}</span>
                            )}
                            {actualWidth < 4 && (
                              <div className="absolute right-0 top-0 h-full flex items-center">
                                <span className="text-xs font-bold text-white drop-shadow-lg bg-black/70 px-1.5 py-0.5 rounded whitespace-nowrap">{phase.count}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="w-full mt-4">
                    <div className="flex items-start justify-between gap-2">
                      {orderedPhases.map((phase) => {
                        const phasePercentage = total > 0 ? ((phase.count / total) * 100).toFixed(1) : '0.0';
                        return (
                          <div
                            key={phase.key}
                            className="flex-1 flex flex-col items-center text-center min-w-0"
                            style={{ flexBasis: `${100 / Math.max(orderedPhases.length, 1)}%` }}
                          >
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">{phase.label}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{phase.count} ({phasePercentage}%)</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Conversations</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{data.totalConversations || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">New Conversations</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{data.newConversations || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">CTAs Shared</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{data.ctasShared || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Conversions</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{data.conversions || 0}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Conversion Rate</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{data.conversionRate || 0}%</p>
                </div>
              </div>
            </div>
          );
        };

        return (
          <div className="card">
            <div className="card-header">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Phase Statistics {phaseStats && `- ${phaseStats.modelName}`}
                {splitByVersion && <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">· by chatbot version</span>}
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                {splitByVersion
                  ? 'Each release (V2, V3, …) has its own phase space — shown separately below.'
                  : 'Conversation distribution by phase'}
              </p>
            </div>
            <div className="card-body">
              {phaseStatsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="loading-spinner h-8 w-8"></div>
                </div>
              ) : phaseStats ? (
                splitByVersion ? (
                  <div className="space-y-8 divide-y divide-gray-200 dark:divide-gray-700 [&>*:not(:first-child)]:pt-8">
                    {versionsWithData.map((v) => renderPhaseDistribution(v, v.slug))}
                  </div>
                ) : (
                  renderPhaseDistribution(phaseStats, null)
                )
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No phase statistics available for this model
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* CTA Links Performance */}
      {selectedModelId && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <LinkIcon className="h-6 w-6 text-purple-500" />
              CTA Links Performance
            </h2>
            <p className="text-gray-600 dark:text-gray-300">Track performance of each CTA link</p>
          </div>
          <div className="card-body">
            {linkAnalyticsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="loading-spinner h-8 w-8"></div>
              </div>
            ) : linkAnalytics && linkAnalytics.links && linkAnalytics.links.length > 0 ? (
              <div className="space-y-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg">
                  <div className="text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Total Links Shared</p>
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{linkAnalytics.totals?.timesSent || 0}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Total Conversions</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{linkAnalytics.totals?.conversions || 0}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Overall Conversion Rate</p>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{linkAnalytics.totals?.conversionRate || 0}%</p>
                  </div>
                </div>

                {/* Individual Link Stats */}
                <div className="space-y-3">
                  {linkAnalytics.links.map((link, index) => (
                    <div 
                      key={index} 
                      className={`p-4 rounded-lg border ${link.isPrimary 
                        ? 'bg-gradient-to-r from-blue-50 to-sky-50 dark:from-blue-900/20 dark:to-sky-900/20 border-blue-200 dark:border-blue-700' 
                        : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                            link.isPrimary 
                              ? 'bg-gradient-to-br from-blue-500 to-sky-500' 
                              : 'bg-gradient-to-br from-green-500 to-teal-500'
                          }`}>
                            {index + 1}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                link.isPrimary 
                                  ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' 
                                  : 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400'
                              }`}>
                                {link.label}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-300 truncate" title={link.url}>
                              {link.url}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-6 ml-4">
                          <div className="text-center">
                            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{link.analytics?.timesSent || 0}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Times Sent</p>
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-bold text-green-600 dark:text-green-400">{link.analytics?.conversions || 0}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Conversions</p>
                          </div>
                          <div className="text-center min-w-[60px]">
                            <p className={`text-lg font-bold ${
                              (link.analytics?.conversionRate || 0) >= 10 
                                ? 'text-green-600 dark:text-green-400' 
                                : (link.analytics?.conversionRate || 0) >= 5 
                                  ? 'text-yellow-600 dark:text-yellow-400'
                                  : 'text-gray-600 dark:text-gray-400'
                            }`}>
                              {link.analytics?.conversionRate || 0}%
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Conv. Rate</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Progress bar showing relative performance */}
                      {linkAnalytics.totals?.timesSent > 0 && (
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                            <span>Usage share</span>
                            <span>{Math.round((link.analytics?.timesSent || 0) / linkAnalytics.totals.timesSent * 100)}%</span>
                          </div>
                          <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${link.isPrimary ? 'bg-gradient-to-r from-blue-500 to-sky-500' : 'bg-gradient-to-r from-green-500 to-teal-500'}`}
                              style={{ width: `${Math.round((link.analytics?.timesSent || 0) / linkAnalytics.totals.timesSent * 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Info message */}
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-xs text-blue-600 dark:text-blue-300 flex items-start gap-2">
                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Links are randomly selected when sending CTAs. Add more links in Model Settings → CTA Information to A/B test different platforms.</span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <LinkIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">No CTA links configured</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Configure CTA links in Model Settings to track their performance.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Accounts"
          value={dashboardData?.totalAccounts || 0}
          icon={UserGroupIcon}
          color="blue"
          loading={overviewLoading}
        />
        <StatCard
          title="Active Accounts"
          value={dashboardData?.activeAccounts || 0}
          icon={ChartBarIcon}
          color="green"
          loading={overviewLoading}
        />
        <StatCard
          title="Total Responses"
          value={dashboardData?.totalResponses || 0}
          icon={ChatBubbleLeftRightIcon}
          color="purple"
          loading={overviewLoading}
        />
        <StatCard
          title="Conversion Rate"
          value={`${dashboardData?.conversionRate || 0}%`}
          icon={CursorArrowRaysIcon}
          color="orange"
          loading={overviewLoading}
        />
      </div>

      {/* CTA Analytics Section */}
      {ctaAnalytics && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">CTA Performance Analytics</h2>
            <p className="text-gray-600 dark:text-gray-300">Track CTA script effectiveness and conversion rates</p>
          </div>

          {/* CTA Stats Grid - Removed: Total Conversions, Avg Time to Conversion, Follow-up Response Rate, Follow-up Conversion Rate */}

          {/* Best Performing Scripts */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Best Performing CTA Scripts</h3>
            </div>
            <div className="card-body">
              {ctaAnalytics.bestPerformingScripts && ctaAnalytics.bestPerformingScripts.length > 0 ? (
                <div className="space-y-4">
                  {ctaAnalytics.bestPerformingScripts.map((script, index) => (
                    <div key={script.scriptNumber} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <div className="flex items-center justify-center w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full">
                            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">#{index + 1}</span>
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{script.name}</h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Model: {script.modelName}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-6">
                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{script.analytics.conversionRate.toFixed(1)}%</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Conversion Rate</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{script.analytics.totalConversions}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Conversions</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{script.analytics.totalSent}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Total Sent</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrophyIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">No CTA scripts found</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">CTA performance data will appear here once scripts are used.</p>
                </div>
              )}
            </div>
          </div>

          {/* Follow-up Effectiveness - Removed */}
        </div>
      )}

      {/* Charts Section - Removed: Responses Over Time, Conversion Rate */}

      {/* No-Response Follow-up Analytics */}
      {selectedModelId && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-500" />
              No-Response Follow-up Performance
            </h2>
            <p className="text-gray-600 dark:text-gray-300">Response rates for follow-up messages sent when users don't respond for 30 minutes</p>
          </div>
          <div className="card-body">
            {followUpAnalyticsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="loading-spinner h-8 w-8"></div>
              </div>
            ) : followUpAnalytics && Array.isArray(followUpAnalytics.messages) ? (
              <div className="space-y-4">
                {/* Summary Stats */}
                {followUpAnalytics.totals && (
                  <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg mb-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{followUpAnalytics.totals.totalSent || 0}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total Sent</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">{followUpAnalytics.totals.totalResponses || 0}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total Responses</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                        {followUpAnalytics.totals.averageResponseRate?.toFixed(2) || 0}%
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Avg Response Rate</p>
                    </div>
                  </div>
                )}
                
                {/* Message Performance */}
                {followUpAnalytics.messages && followUpAnalytics.messages.length > 0 ? (
                  <div className="space-y-3">
                    {followUpAnalytics.messages.slice(0, followUpAnalyticsDisplayCount).map((msg, index) => (
                      <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="mb-3">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Message:</p>
                            {msg.followUpType && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                                {msg.followUpType === 'afterCta' ? 'after CTA' : 'before CTA'}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300">{msg.messageText}</p>
                        </div>
                        <div className="grid grid-cols-4 gap-3 mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                          <div className="text-center">
                            <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{msg.timesSent || 0}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Times Sent</p>
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-bold text-green-600 dark:text-green-400">{msg.responses || 0}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Responses</p>
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-bold text-primary-600 dark:text-primary-400">
                              {msg.responseRate?.toFixed(2) || 0}%
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Response Rate</p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {msg.lastUsed ? new Date(msg.lastUsed).toLocaleDateString() : 'Never'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Last Used</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {followUpAnalytics.messages.length > followUpAnalyticsDisplayCount && (
                      <div className="flex justify-center pt-2">
                        <button
                          onClick={() => setFollowUpAnalyticsDisplayCount((prev) => Math.min(prev + 5, followUpAnalytics.messages.length))}
                          className="px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 border border-primary-300 dark:border-primary-700 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                        >
                          See more ({followUpAnalytics.messages.length - followUpAnalyticsDisplayCount} remaining)
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    <p>No follow-up messages sent yet for this model in the selected date range.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">No follow-up data yet</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Follow-up performance will appear here once messages are sent.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mass DM Analytics - Only show if user has permission */}
      {user?.features?.canUseMassDM && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Mass DM Message Performance</h3>
            <p className="text-gray-600 dark:text-gray-300">Response rates by message content</p>
          </div>
          <div className="card-body">
            {massDMAnalyticsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="loading-spinner h-8 w-8"></div>
              </div>
            ) : massDMAnalytics && massDMAnalytics.length > 0 ? (
              <div className="space-y-3">
                {massDMAnalytics.slice(0, massDMAnalyticsDisplayCount).map((analytics, index) => (
                  <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="mb-2">
                      <p className="text-xs font-medium text-gray-900 dark:text-gray-100 mb-1">Message:</p>
                      <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap line-clamp-2">{analytics.messageContent}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                      <div className="text-center">
                        <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{analytics.totalSent}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total Sent</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-green-600 dark:text-green-400">{analytics.conversationsStarted}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Conversations Started</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-primary-600 dark:text-primary-400">
                          {analytics.responseRate.toFixed(2)}%
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Response Rate</p>
                      </div>
                    </div>
                  </div>
                ))}
                {massDMAnalytics.length > massDMAnalyticsDisplayCount && (
                  <div className="flex justify-center pt-2">
                    <button
                      onClick={() => setMassDMAnalyticsDisplayCount(prev => Math.min(prev + 5, massDMAnalytics.length))}
                      className="px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 border border-primary-300 dark:border-primary-700 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                    >
                      Load More ({massDMAnalytics.length - massDMAnalyticsDisplayCount} remaining)
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">No mass DM analytics yet</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Analytics will appear here once mass DMs are sent and conversations start.</p>
              </div>
            )}
          </div>
        </div>
      )}
      </>
      )}

    </div>
  );
};

export default Analytics;
